import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import Preloader from '../../components/Preloader';
import api from '../../services/api';

const DoctorBatches = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [caseProgress, setCaseProgress] = useState({});

  useEffect(() => {
    if (user) {
      setLoading(true);
      loadMyBatches();
    }
  }, [user]);

  const loadMyBatches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/batches/my-batches');
      const myBatches = response.data.batches || [];
      
      // Process batches to add additional info
      const processedBatches = myBatches.map(batch => ({
        ...batch,
        statusInfo: getBatchStatusInfo(batch),
        deadlineInfo: getDeadlineInfo(batch),
        canStart: batch.can_start
      }));
      
      setBatches(processedBatches);
      
      // Fetch case progress for each batch in parallel
      const progressPromises = myBatches.map(batch =>
        api.get(`/batch-assignments/batch/${batch.id}/status`)
          .then(statusRes => {
            console.log(`[DoctorBatches] Progress fetch for batch ${batch.id}:`, statusRes.data.my_assignments);
            return {
              batchId: batch.id,
              progress: statusRes.data.my_assignments || { total: 0, completed: 0, remaining: 0, completion_percentage: 0 }
            };
          })
          .catch(err => {
            console.warn(`[DoctorBatches] Could not fetch progress for batch ${batch.id}:`, err.message);
            return {
              batchId: batch.id,
              progress: { total: 0, completed: 0, remaining: 0, completion_percentage: 0 }
            };
          })
      );
      
      const progressResults = await Promise.all(progressPromises);
      const progressMap = {};
      progressResults.forEach(result => {
        progressMap[result.batchId] = result.progress;
      });
      
      setCaseProgress(progressMap);
    } catch (err) {
      console.error('Error loading my batches:', err);
      setError('Failed to load batches: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getBatchStatusInfo = (batch) => {
    const now = new Date();
    const startDate = new Date(batch.start_date);
    const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
    const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
    
    if (now < startDate) {
      return { text: 'Future', class: 'text-secondary', icon: 'fa-clock' };
    } else if (now > graceDate) {
      return { text: 'Expired', class: 'text-danger', icon: 'fa-times-circle' };
    } else if (now > endDate) {
      return { text: 'Grace Period', class: 'text-warning', icon: 'fa-exclamation-triangle' };
    } else {
      return { text: 'Active', class: 'text-success', icon: 'fa-check-circle' };
    }
  };

  const getDeadlineInfo = (batch) => {
    const now = new Date();
    const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
    const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
    
    if (now < new Date(batch.start_date)) {
      return { text: `Starts ${new Date(batch.start_date).toLocaleDateString()}`, type: 'future' };
    } else if (now > graceDate) {
      const expiredHours = Math.floor((now - graceDate) / (1000 * 60 * 60));
      return { text: `Expired ${expiredHours}h ago`, type: 'expired' };
    } else if (now > endDate) {
      const remainingHours = Math.floor((graceDate - now) / (1000 * 60 * 60));
      return { text: `${remainingHours}h left (Grace Period)`, type: 'grace' };
    } else {
      const remainingHours = Math.floor((endDate - now) / (1000 * 60 * 60));
      return { text: `${remainingHours}h remaining`, type: 'active' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getEffectiveEndDate = (batch) => batch.extended_end_date || batch.end_date;

  const handleViewCases = (batch) => {
    if (batch.canStart) {
      navigate('/doctor/cases?batch=' + batch.id);
    }
  };

  const handleViewCasesProtocol = (batch, protocolId) => {
    if (batch.canStart) {
      navigate(`/doctor/cases?batch=${batch.id}&protocol=${protocolId}`);
    }
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="My Batches">
        <Preloader message="Loading your batches..." />
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="My Batches">
      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h4 className="mb-1">
                  <i className="fas fa-folder-open me-2"></i>
                  My Batch Assignments
                </h4>
                <small className="opacity-75">
                  {batches.length} batch{batches.length !== 1 ? 'es' : ''} assigned to you
                </small>
              </div>
              <div className="card-body">
                {batches.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-folder-open fa-4x text-muted mb-4"></i>
                    <h4 className="text-muted">No Batches Assigned</h4>
                    <p className="text-muted">
                      You haven't been assigned to any batches yet. 
                      Your Medical Admin will assign you to batches as they become available.
                    </p>
                  </div>
                ) : (
                  <div className="row">
                    {batches.map((batch) => (
                      <div key={batch.id} className="col-lg-6 col-md-12 mb-4">
                        <div className="card h-100 border-2">
                          <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-primary fw-bold">
                              <i className="fas fa-folder me-2"></i>
                              {batch.batch_number || batch.name}
                            </h5>
                            <div className="d-flex gap-2">
                              <span className={`badge ${getBatchStatusInfo(batch).class === 'text-success' ? 'bg-success' : 
                                               getBatchStatusInfo(batch).class === 'text-warning' ? 'bg-warning' :
                                               getBatchStatusInfo(batch).class === 'text-danger' ? 'bg-danger' : 'bg-secondary'}`}>
                                <i className={`fas ${getBatchStatusInfo(batch).icon} me-1`}></i>
                                {getBatchStatusInfo(batch).text}
                              </span>
                            </div>
                          </div>
                          <div className="card-body">
                            {/* Batch Description */}
                            {batch.description && (
                              <p className="text-muted mb-3">{batch.description}</p>
                            )}

                            {/* Timeline */}
                            <div className="row mb-3">
                              <div className="col-6">
                                <small className="text-muted d-block">Start Date</small>
                                <div className="fw-bold small">
                                  <i className="fas fa-play text-success me-1"></i>
                                  {formatDate(batch.start_date)}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">End Date</small>
                                <div className="fw-bold small">
                                  <i className="fas fa-stop text-danger me-1"></i>
                                  {formatDate(getEffectiveEndDate(batch))}
                                </div>
                              </div>
                            </div>

                            {/* Case Progress Section */}
                            {caseProgress[batch.id] ? (
                              <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className="text-muted fw-bold d-block">
                                    <i className="fas fa-tasks me-1"></i>
                                    Case Progress
                                  </small>
                                  <small className="badge bg-info">
                                    {caseProgress[batch.id].completed || 0}/{caseProgress[batch.id].total || 0}
                                  </small>
                                </div>
                                
                                {/* Progress Bar */}
                                <div className="progress mb-2" style={{ height: '24px' }}>
                                  <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{
                                      width: `${caseProgress[batch.id].completion_percentage || 0}%`
                                    }}
                                    aria-valuenow={caseProgress[batch.id].completed || 0}
                                    aria-valuemin="0"
                                    aria-valuemax={caseProgress[batch.id].total || 0}
                                  >
                                    {caseProgress[batch.id].total > 0 && (
                                      <small className="text-white fw-bold">
                                        {Math.round(caseProgress[batch.id].completion_percentage || 0)}%
                                      </small>
                                    )}
                                  </div>
                                </div>

                                {/* Progress Details */}
                                {caseProgress[batch.id].total > 0 ? (
                                  <div className="row g-2">
                                    <div className="col-6">
                                      <div className="p-2 bg-success bg-opacity-10 rounded text-center">
                                        <small className="text-muted d-block">
                                          <i className="fas fa-check-circle text-success me-1"></i>
                                          Completed
                                        </small>
                                        <div className="fw-bold text-success">
                                          {caseProgress[batch.id].completed || 0} Cases
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <div className="p-2 bg-warning bg-opacity-10 rounded text-center">
                                        <small className="text-muted d-block">
                                          <i className="fas fa-hourglass-end text-warning me-1"></i>
                                          Remaining
                                        </small>
                                        <div className="fw-bold text-warning">
                                          {caseProgress[batch.id].remaining || 0} Cases
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <small className="text-muted">
                                    <i className="fas fa-info-circle me-1"></i>
                                    No cases assigned yet
                                  </small>
                                )}
                              </div>
                            ) : null}

                            {/* Case Count */}
                            <div className="row mb-3">
                              <div className="col-6">
                                <small className="text-muted d-block">Cases in Batch</small>
                                <div className="fw-bold text-primary">
                                  <i className="fas fa-file-medical me-1"></i>
                                  {batch.case_count || 0} Cases
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">My Completed</small>
                                <div className="fw-bold text-info">
                                  <i className="fas fa-check-circle me-1"></i>
                                  {batch.my_case_count || 0} Cases
                                </div>
                              </div>
                            </div>

                            {/* Deadline Warning */}
                            <div className="mb-3">
                              <div className={`p-2 rounded ${
                                getDeadlineInfo(batch).type === 'active' ? 'bg-success bg-opacity-10' :
                                getDeadlineInfo(batch).type === 'grace' ? 'bg-warning bg-opacity-10' :
                                getDeadlineInfo(batch).type === 'future' ? 'bg-info bg-opacity-10' :
                                'bg-danger bg-opacity-10'
                              }`}>
                                <small className={`fw-bold ${
                                  getDeadlineInfo(batch).type === 'active' ? 'text-success' :
                                  getDeadlineInfo(batch).type === 'grace' ? 'text-warning' :
                                  getDeadlineInfo(batch).type === 'future' ? 'text-info' :
                                  'text-danger'
                                }`}>
                                  <i className={`fas ${
                                    getDeadlineInfo(batch).type === 'active' ? 'fa-clock' :
                                    getDeadlineInfo(batch).type === 'grace' ? 'fa-exclamation-triangle' :
                                    getDeadlineInfo(batch).type === 'future' ? 'fa-calendar' :
                                    'fa-times-circle'
                                  } me-1`}></i>
                                  {getDeadlineInfo(batch).text}
                                </small>
                                {getDeadlineInfo(batch).type === 'grace' && (
                                  <div className="mt-1">
                                    <small className="text-warning">
                                      <i className="fas fa-info-circle me-1"></i>
                                      Grace period active - complete remaining cases
                                    </small>
                                  </div>
                                )}
                                {getDeadlineInfo(batch).type === 'expired' && (
                                  <div className="mt-1">
                                    <small className="text-danger">
                                      <i className="fas fa-lock me-1"></i>
                                      Batch expired - completed work saved
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Grace Period Info */}
                            {batch.grace_period_hours > 0 && (
                              <div className="mb-3">
                                <small className="text-muted">
                                  <i className="fas fa-shield-alt me-1"></i>
                                  Grace Period: {batch.grace_period_hours} hours
                                </small>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="d-grid gap-2">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handleViewCases(batch)}
                                disabled={!batch.canStart}
                              >
                                <i className="fas fa-file-medical me-2"></i>
                                {batch.canStart ? 'View My Cases' : 
                                 getBatchStatusInfo(batch).text === 'Future' ? 'Not Started Yet' :
                                 getBatchStatusInfo(batch).text === 'Expired' ? 'Batch Expired' : 'View Cases (Read-Only)'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="text-primary mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  Understanding Batch Status
                </h6>
                <div className="row">
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-success me-2">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <strong>Active</strong>
                    </div>
                    <small className="text-muted">You can work on cases in this batch</small>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-warning me-2">
                        <i className="fas fa-exclamation-triangle"></i>
                      </span>
                      <strong>Grace Period</strong>
                    </div>
                    <small className="text-muted">Deadline passed but you can still work</small>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-secondary me-2">
                        <i className="fas fa-clock"></i>
                      </span>
                      <strong>Future</strong>
                    </div>
                    <small className="text-muted">Batch hasn't started yet</small>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-danger me-2">
                        <i className="fas fa-times-circle"></i>
                      </span>
                      <strong>Expired</strong>
                    </div>
                    <small className="text-muted">No new work, completed work saved</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default DoctorBatches;